import {useMemo} from 'react'

import {type Theme, useTheme} from '#/alf'

/**
 * The furniture a document surface draws around its pages, in the app's colours.
 *
 * A document reader is a page on a canvas, and everything around the page — the
 * ground it floats on, the controls in the header and the footer, the rail of
 * thumbnails, the lines between them — is the *app's* furniture, not the paper's.
 * So it follows the app's mode, the way a dialog does.
 *
 * The distinction that makes this its own vocabulary rather than `useTheme` at
 * each call site is that a document surface has two grounds, not one. Its
 * furniture is sometimes drawn on its own canvas (the reader) and sometimes on
 * the *paper itself* (the controls that sit over a page in the card, which are
 * dark in both modes because the page under them is white in both — that set is
 * the reader's and the card's own, and lives with those components). This hook is
 * only the first of the two.
 *
 * The two modes invert differently, and that inversion is the whole reason the
 * values are here instead of at the call sites:
 *
 *  - **Dark** floats a lit sheet on the app's own dark page. The page is the
 *    brightest thing on screen, so it needs no edge, and a control is a wash of
 *    light — an alpha, which composites correctly over whatever is behind it.
 *  - **Light** sets white paper on a grey canvas, because white on white has no
 *    edge at all: the canvas does the work the glow does in the dark. A control
 *    is then a white fill, and alpha would make it *lighter* than the canvas it
 *    sits on, which reads as a hole rather than a button — so it gets a hairline
 *    to say where it ends (`surfaceEdge`).
 */
export type DocumentChrome = {
  /** The ground the pages float on. */
  canvas: string
  /** A control's fill, and the fill of a line of copy that has to look pressable. */
  surface: string
  /**
   * The line that gives `surface` an edge, where the fill alone does not.
   *
   * Absent in the dark: a white wash on black already has one.
   */
  surfaceEdge?: string
  /** A control that is on — a selected thumbnail, an open panel's own button. */
  surfaceOn: string
  /** A page-shaped hole that has not been painted yet. */
  placeholder: string
  /** The shadow that makes a page read as a sheet, where the page needs one. */
  sheet?: string
  /** Ink on `surface`. */
  ink: string
  /** Supporting ink on `surface`. */
  inkMuted: string
  /** A hairline drawn on the canvas. */
  hairline: string
}

/** The chrome for a theme. Exported for the tests, which assert both modes. */
export function chromeFor(theme: Theme): DocumentChrome {
  if (theme.scheme === 'light') {
    return {
      canvas: theme.atoms.bg_contrast_50.backgroundColor,
      surface: theme.atoms.bg.backgroundColor,
      surfaceEdge: theme.atoms.border_contrast_low.borderColor,
      surfaceOn: theme.atoms.bg_contrast_100.backgroundColor,
      placeholder: 'rgba(0, 0, 0, 0.05)',
      // A sheet of paper on a pale canvas: without this the page is a rectangle
      // the same near-white as the room it is lying in.
      sheet: '0 1px 2px rgba(0, 0, 0, 0.12), 0 6px 16px rgba(0, 0, 0, 0.08)',
      ink: theme.atoms.text.color,
      inkMuted: theme.atoms.text_contrast_medium.color,
      hairline: theme.atoms.border_contrast_low.borderColor,
    }
  }
  return {
    canvas: theme.atoms.bg.backgroundColor,
    surface: 'rgba(255, 255, 255, 0.12)',
    surfaceOn: 'rgba(255, 255, 255, 0.24)',
    placeholder: 'rgba(255, 255, 255, 0.06)',
    ink: theme.atoms.text.color,
    inkMuted: 'rgba(255, 255, 255, 0.6)',
    hairline: 'rgba(255, 255, 255, 0.14)',
  }
}

export function useDocumentChrome(): DocumentChrome {
  const t = useTheme()
  return useMemo(() => chromeFor(t), [t])
}
